import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight, Ruler, X, Plus, Check } from "lucide-react";
import Reveal from "../components/Reveal";
import ProductTile from "../components/ProductTile";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { Container, PageHeader } from "../components/ui-kit";
import { useQuote } from "../context/QuoteCart";
import { CATEGORIES } from "../mock";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogClose, DialogTitle, DialogDescription } from "../components/ui/dialog";

const Products = () => {
  const [params, setParams] = useSearchParams();
  const active = params.get("kategori") || "all";
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // {item, cat}

  useEffect(() => {
    setSelected(null);
  }, [active]);

  const visibleCats = useMemo(() => {
    const cats = active === "all" ? CATEGORIES : CATEGORIES.filter((c) => c.id === active);
    if (!query.trim()) return cats;
    const q = query.toLocaleLowerCase("tr");
    return cats
      .map((c) => ({
        ...c,
        items: c.items.filter(
          (it) => it.name.toLocaleLowerCase("tr").includes(q) || (it.code && it.code.toLowerCase().includes(q))
        ),
      }))
      .filter((c) => c.items.length > 0 || c.name.toLocaleLowerCase("tr").includes(q));
  }, [active, query]);

  const totalItems = visibleCats.reduce((a, c) => a + c.items.length, 0);
  const activeCat = CATEGORIES.find((c) => c.id === active);

  const { has, toggle } = useQuote();
  const q = selected
    ? { name: selected.item.name, category: selected.cat.name, code: selected.item.code || "", image: selected.item.image }
    : null;
  const inCart = q ? has(q) : false;
  const addToCart = () => {
    if (!q) return;
    toggle(q);
    if (inCart) {
      toast("Teklif sepetinden çıkarıldı", { description: q.name });
    } else {
      toast.success("Teklif sepetine eklendi", { description: q.name });
    }
  };

  return (
    <main className="bg-canvas min-h-screen" data-testid="products-page">
      <Seo
        title={activeCat ? `${activeCat.name.charAt(0) + activeCat.name.slice(1).toLocaleLowerCase("tr")} | Ürünler — Koşan Düğme` : "Ürünler | Çıtçıt, Kot Düğme, Toka, Kuşgözü — Koşan Düğme"}
        description={activeCat ? activeCat.desc : "12 ana grupta 1000+ metal tekstil aksesuarı: çıtçıt, kuşgözü, perçin, kot düğme, toka, stoper ve bağ ucu. Kendi tesisimizde 48 kaplama renginde üretim."}
        path="/urunler"
      />
      <Breadcrumbs
        items={
          activeCat
            ? [{ label: "Ürünler", to: "/urunler" }, { label: activeCat.name.charAt(0) + activeCat.name.slice(1).toLocaleLowerCase("tr"), to: `/urunler?kategori=${activeCat.id}` }]
            : [{ label: "Ürünler", to: "/urunler" }]
        }
      />
      <PageHeader
        eyebrow="Katalog 2024"
        title={activeCat ? activeCat.name.charAt(0) + activeCat.name.slice(1).toLocaleLowerCase("tr") : "Ürünlerimiz"}
        text={
          activeCat
            ? activeCat.desc
            : "Çıtçıttan klikite, kuşgözünden tokaya — 12 ana grupta 1000'den fazla ürün çeşidi. Tüm ürünlerimiz kendi tesisimizde üretilir ve renk kartelamızdaki tüm kaplamalarda sunulur."
        }
      >
        <div className="hidden lg:grid grid-cols-4 gap-2" data-testid="products-header-tiles">
          {(activeCat ? activeCat.items : CATEGORIES).slice(0, 4).map((c, i) => (
            <div key={i} className="aspect-square bg-plate border border-line overflow-hidden">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </PageHeader>

      {/* Filtre çubuğu */}
      <div className="sticky top-[78px] z-30 bg-canvas/90 backdrop-blur-xl border-b border-line" data-testid="products-filter-bar">
        <Container className="py-4 flex flex-col lg:flex-row gap-4 lg:items-center">
          <div className="flex gap-2 overflow-x-auto no-scrollbar lg:flex-wrap lg:overflow-visible flex-1 -mx-1 px-1" data-testid="category-filters">
            <button
              onClick={() => setParams({})}
              data-testid="filter-all"
              className={`shrink-0 h-9 px-4 rounded-full text-[11px] font-semibold tracking-[0.12em] uppercase border transition-all duration-300 ${
                active === "all" ? "bg-ink border-ink text-white" : "bg-white border-line text-ink2 hover:border-ink hover:text-ink"
              }`}
            >
              Tümü
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setParams({ kategori: c.id })}
                data-testid={`filter-${c.id}`}
                className={`shrink-0 h-9 px-4 rounded-full text-[11px] font-semibold tracking-[0.12em] uppercase border transition-all duration-300 ${
                  active === c.id ? "bg-ink border-ink text-white" : "bg-white border-line text-ink2 hover:border-ink hover:text-ink"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72 shrink-0">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-mute" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ürün veya kod ara… (ör. K-0021)"
              data-testid="product-search-input"
              className="w-full h-10 pl-10 pr-9 rounded-full bg-white border border-line text-sm text-ink focus:outline-none focus:border-ink placeholder:text-mute transition-colors"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-mute hover:text-ink" data-testid="product-search-clear">
                <X size={14} />
              </button>
            )}
          </div>
        </Container>
      </div>

      {/* Ürün listesi */}
      <Container className="py-14 lg:py-20">
        <p className="text-[11px] text-mute tracking-[0.2em] uppercase font-semibold mb-12" data-testid="products-count">
          {totalItems} ürün listeleniyor
        </p>

        {visibleCats.length === 0 && (
          <div className="bg-paper border border-line p-16 text-center" data-testid="products-empty">
            <p className="font-display text-xl text-ink">Sonuç bulunamadı</p>
            <p className="text-sm text-mute mt-2">Farklı bir anahtar kelime veya kategori deneyin.</p>
          </div>
        )}

        {visibleCats.map((cat) => (
          <section key={cat.id} className="mb-20 last:mb-0" data-testid={`category-section-${cat.id}`}>
            <Reveal>
              <div className="grid lg:grid-cols-12 gap-6 items-end mb-8">
                <div className="lg:col-span-8">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-px bg-brand" />
                    <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-brand">{cat.items.length} ürün</span>
                  </div>
                  <h2 className="font-display font-light text-3xl sm:text-4xl text-ink mt-3">
                    {cat.name.charAt(0) + cat.name.slice(1).toLocaleLowerCase("tr")}
                  </h2>
                </div>
                <p className="lg:col-span-4 text-ink2 text-[14px] leading-relaxed">{cat.desc}</p>
              </div>
            </Reveal>

            <div
              className={`lattice grid ${
                cat.individual ? "grid-cols-3 sm:grid-cols-4 lg:grid-cols-6" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
              }`}
            >
              {cat.items.map((it, i) => (
                <Reveal key={it.name + i} delay={(i % 6) * 40} className="h-full">
                  <ProductTile item={it} cat={cat} compact={!!cat.individual} onClick={() => setSelected({ item: it, cat })} />
                </Reveal>
              ))}
            </div>
          </section>
        ))}
      </Container>

      {/* Ürün detay */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-paper border border-line rounded-none shadow-[0_40px_100px_rgba(26,26,26,0.18)]" data-testid="product-dialog">
          {selected && (
            <div className="grid sm:grid-cols-2">
              <div className="bg-plate relative">
                <img src={selected.item.image} alt={selected.item.name} className="w-full aspect-square object-cover" data-testid="product-dialog-image" />
                {selected.item.band && (
                  <div className="border-t border-line bg-white p-4">
                    <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-mute mb-2">Katalog görünümü · tüm ölçüler</p>
                    <img src={selected.item.band} alt={`${selected.item.name} ölçüler`} className="w-full max-h-24 object-contain" />
                  </div>
                )}
              </div>
              <div className="p-7 sm:p-9 flex flex-col">
                <DialogTitle className="sr-only">{selected.item.name}</DialogTitle>
                <DialogDescription className="sr-only">{selected.cat.name} — ürün detayı ve teklif ekleme</DialogDescription>
                <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-brand">{selected.cat.name}</p>
                <h3 className="font-display font-light text-2xl sm:text-3xl text-ink mt-3 leading-tight" data-testid="product-dialog-title">
                  {selected.item.name}
                </h3>
                <div className="mt-7">
                  <p className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-mute mb-3">
                    <Ruler size={13} className="text-brand" /> Ölçü / Varyantlar
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="product-dialog-sizes">
                    {selected.item.sizes.map((s) => (
                      <span key={s} className="text-[12px] font-medium bg-sand text-ink px-3 py-1.5 rounded-full border border-line">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                <p className="text-[14px] text-ink2 leading-relaxed mt-7">
                  Bu ürün, renk kartelamızdaki tüm standart, special ve ekolojik kaplamalarda üretilebilir.
                  Özel logo &amp; tasarım talepleriniz için bizimle iletişime geçin.
                </p>
                <div className="mt-auto pt-8">
                  <div className="flex gap-3">
                    <button
                      onClick={addToCart}
                      data-testid="product-dialog-add-quote"
                      className={`group flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full text-[13px] font-semibold transition-colors ${
                        inCart ? "bg-ink text-white hover:bg-ink/90" : "bg-brand hover:bg-brandDark text-white"
                      }`}
                    >
                      {inCart ? (
                        <>Sepete Eklendi <Check size={15} /></>
                      ) : (
                        <>Teklif Sepetine Ekle <Plus size={15} className="group-hover:scale-110 transition-transform" /></>
                      )}
                    </button>
                    <DialogClose asChild>
                      <button className="h-12 px-6 rounded-full border border-line text-[13px] font-semibold text-ink hover:border-ink transition-colors" data-testid="product-dialog-close">
                        Kapat
                      </button>
                    </DialogClose>
                  </div>
                  <Link
                    to={`/iletisim?urun=${encodeURIComponent(selected.item.name)}`}
                    data-testid="product-dialog-quote-link"
                    className="group mt-4 inline-flex items-center gap-1.5 text-[12.5px] text-ink2 hover:text-brand transition-colors"
                  >
                    veya bu ürün için doğrudan teklif isteyin
                    <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Products;
